import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/store";

export interface CustomerFeedbackReducerInterface {
  state: Nullable<CustomerFeedbackStateType[]>;
  selfAssessmentState: Nullable<CustomerFeedbackStateType[]>;
  topics: Nullable<string>[];
  starts: Nullable<Date>;
  stops: Nullable<Date>;
  userIds: string[];
  filterAttrs: Record<string, AttributeFilterType>;
  predefinedFilters: { name: string; params: any }[];
  inactiveTopics: boolean;
}

const initialState: CustomerFeedbackReducerInterface = {
  state: null,
  selfAssessmentState: null,
  topics: [],
  starts: null,
  stops: null,
  userIds: [],
  filterAttrs: {},
  predefinedFilters: [],
  inactiveTopics: false,
};

const customerFeedbackSlice = createSlice({
  name: "customerFeedback",
  initialState,
  reducers: {
    setStatus(state, action: PayloadAction<CustomerFeedbackStateType[]>) {
      state.state = action.payload;
    },
    setSelfAssessmentState(state, action: PayloadAction<CustomerFeedbackStateType[]>) {
      state.selfAssessmentState = action.payload;
    },
    setTopics(state, action: PayloadAction<Nullable<string>[]>) {
      state.topics = action.payload;
    },
    setStarts(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.starts = payload;
    },
    setStops(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.stops = payload;
    },

    setUserIds(state, { payload }: PayloadAction<string[]>) {
      state.userIds = payload;
    },

    setAttributeFilter(state, action: PayloadAction<{ name: string; value: Nilable<AttributeFilterType> }>) {
      if (!action.payload.value) {
        delete state.filterAttrs[action.payload.name];
      } else {
        state.filterAttrs[action.payload.name] = action.payload.value;
      }
    },

    setInActiveTopics(state, action: PayloadAction<boolean>) {
      state.inactiveTopics = action.payload;
    },

    addPredefinedFilter(state, action: PayloadAction<{ name: string; params: any }>) {
      state.predefinedFilters.push(action.payload);
    },

    setPredefinedFilter(state, action: PayloadAction<{ name: string; params: any }>) {
      const idx = state.predefinedFilters.findIndex((el) => el.name === action.payload.name);

      if (idx === -1) {
        state.predefinedFilters.push(action.payload);
      } else {
        state.predefinedFilters[idx].params = action.payload.params;
      }
    },

    removePredefinedFilter(state, action: PayloadAction<string>) {
      state.predefinedFilters = state.predefinedFilters.filter((item) => item.name !== action.payload);
    },

    resetFilters(state) {
      state.state = null;
      state.selfAssessmentState = null;
      state.topics = [];
      state.starts = null;
      state.stops = null;
      state.userIds = [];
      state.filterAttrs = {};
      state.predefinedFilters = [];
    },
  },
});

export const {
  setStatus,
  setSelfAssessmentState,
  setTopics,
  setStarts,
  setStops,
  setAttributeFilter,
  resetFilters,
  setUserIds,
  setInActiveTopics,
  addPredefinedFilter,
  setPredefinedFilter,
  removePredefinedFilter,
} = customerFeedbackSlice.actions;
export const customerFeedbackSelector = (state: RootState) => state.customerFeedback;
export default customerFeedbackSlice.reducer;
